import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminService } from '../../shared/services';

// Thunk para login de administrador
export const adminLogin = createAsyncThunk(
  'adminAuth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await adminService.login(credentials);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Error al iniciar sesión');
    }
  }
);

// Thunk para logout de administrador
export const adminLogout = createAsyncThunk(
  'adminAuth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await adminService.logout();
      return true;
    } catch (error) {
      // Siempre limpiar localStorage incluso si hay error
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  isAuthenticated: false,
  adminData: null,
  token: null,
  loading: false,
  error: null,
  isInitialized: false,
};

// Función para cargar estado inicial desde localStorage
const loadInitialState = () => {
  try {
    const savedToken = localStorage.getItem('adminToken');
    const savedAdminData = localStorage.getItem('adminData');
    
    if (savedToken && savedAdminData) {
      return {
        ...initialState,
        isAuthenticated: true,
        token: savedToken,
        adminData: JSON.parse(savedAdminData),
        isInitialized: true,
      };
    }
  } catch (error) {
    console.error('Error loading admin auth state from localStorage:', error);
    // Limpiar datos corruptos
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
  }
  
  return {
    ...initialState,
    isInitialized: true,
  };
};

const adminAuthSlice = createSlice({
  name: 'adminAuth',
  initialState: loadInitialState(),
  reducers: {
    // Limpiar error
    clearAdminError: (state) => {
      state.error = null;
    },
    
    // Resetear estado
    resetAdminAuth: (state) => {
      state.isAuthenticated = false;
      state.adminData = null;
      state.token = null;
      state.error = null;
      
      // Limpiar localStorage
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminData');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login de administrador
      .addCase(adminLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.adminData = action.payload.admin;
        state.token = action.payload.token;
        state.error = null;
        
        // Guardar en localStorage
        localStorage.setItem('adminToken', action.payload.token);
        localStorage.setItem('adminData', JSON.stringify(action.payload.admin));
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.adminData = null;
        state.token = null;
      })
      
      // Logout de administrador
      .addCase(adminLogout.pending, (state) => {
        state.loading = true;
      })
      .addCase(adminLogout.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.adminData = null;
        state.token = null;
        state.error = null;
        
        // Limpiar localStorage
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
      })
      .addCase(adminLogout.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.adminData = null;
        state.token = null;
        state.error = null;
        
        // Limpiar localStorage incluso si hay error
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
      });
  },
});

export const { clearAdminError, resetAdminAuth } = adminAuthSlice.actions;
export default adminAuthSlice.reducer; 